import { push } from '@takeshape/redux-little-router';
import {
  type FormConfig,
  getServiceObjectFields,
  getShapeById,
  type ProjectSchemaJSON,
  SERVICE_OBJECT_PATTERN_NAME,
  type ShapeJSON,
  wrapOneOfObjects
} from '@takeshape/schema';
import { assert, isRecord } from '@takeshape/util';
import handleError from 'app/common/utils/handle-error';
import { showMessage } from 'app/messages/actions';
import { getDataEditorSchema, getDataEditorShape, getSchema } from 'app/schema';
import { SUBMIT_FORM_FAILURE } from 'app/schema-form/action-types';
import { registerForm, type SubmitFormFailureAction } from 'app/schema-form/actions';
import { produce } from 'immer';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import omitBy from 'lodash/omitBy';
import set from 'lodash/set';
import type { SagaIterator } from 'redux-saga';
import { all, call, cancelled, delay, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import type { PayloadAction, PayloadMetaAction, TypeConstant } from 'typesafe-actions';
import { getCurrentBranchPath } from '../../branches/selectors.js';
import type { Requested, Routine } from '../../common/utils/new-routines.js';
import { context } from '../../config.js';
import {
  type ContentMeta,
  type ContentUpdatedAction,
  contentIndexed,
  contentUpdated,
  createContent,
  deleteContent,
  duplicateContent,
  getContent,
  invalidateContent,
  type SaveAction,
  type SaveAndAddAnotherAction,
  type SaveAndContinueAction,
  type SaveAndDuplicateAction,
  type SavePayload,
  save,
  saveAndAddAnother,
  saveAndContinue,
  saveAndDuplicate,
  type UpdatePayload,
  updateContent
} from '../actions';
import {
  type ContentMutationParams,
  type ContentQueryParams,
  createContentMutation,
  deleteContentMutation,
  duplicateContentMutation,
  getContentQuery,
  updateContentMutation
} from '../api';
import { FORM_NAME } from '../constants.js';
import { getContentState, getDefaultContent, getSingletonContentState } from '../selectors.js';
import type { ContentResponse, ContentState } from '../types.js';

export const errorMessages = {
  version:
    "Someone else has saved while you've been editing. " +
    'Copy your changes to your clipboard and then refresh to see the latest version of this content.',
  generic: 'Something has gone wrong. Please refresh and try again.',
  permission: 'Insufficient permissions to perform this action'
};

function fixArrayInput(projectSchema: ProjectSchemaJSON, shape: ShapeJSON, payload: UpdatePayload): UpdatePayload {
  const { schema } = shape;
  return {
    ...payload,
    input: wrapOneOfObjects(projectSchema, schema, payload.input)
  };
}

function contentIndexedPredicate(shapeId: string) {
  return (action: any) => action.type === contentIndexed.toString() && action.payload.shapeId === shapeId;
}

export function* waitForIndexing(shapeId: string) {
  yield race({
    timeout: delay(5000),
    indexed: take(contentIndexedPredicate(shapeId))
  });
}

const NON_NULL_MESSAGE = 'Cannot return null for non-nullable field';

function findNonNullableField(e: { rawError: Error[] }): string | undefined {
  const error = e.rawError?.find((error) => error.message.startsWith(NON_NULL_MESSAGE));
  if (error) {
    return error.message.substring(NON_NULL_MESSAGE.length + 1, error.message.length - 1);
  }
}

export function missingRequiredDataErrorMessage(field: string): string {
  return `Missing required data for ${field}. Shapes with ShapeDB storage should only have properties set to required after the initial data is saved. To fix this make ${field} optional, save a value and then re-enable required.`;
}

export function createPersistenceSaga<
  Meta extends ContentMeta,
  RequestedPayload,
  FulfilledPayload,
  Type extends TypeConstant
>(
  routine: Routine<Type, Meta, RequestedPayload, undefined, FulfilledPayload, string, undefined>,
  handler: (params: ContentQueryParams | ContentMutationParams) => Promise<FulfilledPayload>
) {
  return function* (
    action: PayloadMetaAction<Requested<Type>, RequestedPayload, Meta>
  ): SagaIterator<FulfilledPayload | undefined> {
    const { meta } = action;
    const { redirectOnSuccess, redirectOnFailure, callback } = meta || {};

    const projectSchema: ProjectSchemaJSON = yield select(getSchema);
    const shape = getShapeById(projectSchema, meta.shapeId);
    assert(shape, `Shape not found for shapeId: ${meta.shapeId}`);
    const isSingleton = shape.model?.type === 'single';
    const { shapeId, contentId, locale } = meta;

    if ((action.type as string) !== createContent.REQUESTED) {
      const contentState = yield isSingleton
        ? select(getSingletonContentState, { shapeId, locale })
        : select(getContentState, { shapeId, contentId, locale });
      if (contentState.loading) {
        return;
      }
    }

    try {
      yield put(routine.pending(undefined, meta));

      let inputParams: Partial<UpdatePayload> = {};
      if (isUpdatePayload(action.payload)) {
        const dataEditorShape: [ProjectSchemaJSON, ShapeJSON] = yield select(getDataEditorShape, {
          shapeName: shape.name
        });
        inputParams = fixArrayInput(...dataEditorShape, action.payload);
      }

      const params = {
        ...inputParams,
        projectSchema,
        shapeId,
        contentId,
        locale
      };

      const payload = yield call(handler, params);

      yield put(routine.fulfilled(payload, meta));

      if (redirectOnSuccess) {
        yield put(push(redirectOnSuccess));
      }

      if (callback) {
        yield call(callback, payload.response, payload.contentId);
      }

      if (
        (routine.REQUESTED as string) === updateContent.REQUESTED ||
        (routine.REQUESTED as string) === createContent.REQUESTED
      ) {
        yield put(showMessage('info', 'Saved', 1500));
      }

      return payload;
    } catch (e: any) {
      if (context.tier === 'dev' && e.name !== 'GraphQLError') {
        console.trace(`${action.type} failed`, e.stack);
      }

      if (redirectOnFailure) {
        yield put(push(redirectOnFailure));
      }

      const errorAction = handleError(e);
      if (errorAction) {
        yield put(errorAction);
      } else {
        let errorText = e.message ?? errorMessages.generic;

        if (e.message.indexOf('Invalid version') !== -1) {
          errorText = errorMessages.version;
        } else if ((routine.REQUESTED as string) === getContent.REQUESTED) {
          const field = findNonNullableField(e);
          if (field) {
            errorText = missingRequiredDataErrorMessage(field);
          }
        }

        yield put(showMessage('error', errorText));
      }

      yield put(routine.rejected(e.message, meta));
    } finally {
      if (yield cancelled()) {
        yield put(routine.cancelled(undefined, meta));
      }
    }
  };
}

export const getContentSaga = createPersistenceSaga(getContent, getContentQuery);
export const createContentSaga = createPersistenceSaga(createContent, createContentMutation);
export const updateContentSaga = createPersistenceSaga(updateContent, updateContentMutation);
export const deleteContentSaga = createPersistenceSaga(deleteContent, deleteContentMutation);
export const duplicateContentSaga = createPersistenceSaga(duplicateContent, duplicateContentMutation);

function isUpdatePayload(payload: unknown): payload is UpdatePayload {
  return isRecord(payload) && 'input' in payload && 'structure' in payload;
}

type SaveCallbackParams<Action> = {
  fulfilledPayload: ContentResponse;
  action: Action;
};

type SaveSagaParams<Action> = {
  createCallback?: (params: SaveCallbackParams<Action>) => void;
  updateCallback?: (params: SaveCallbackParams<Action>) => void;
};

export function getServiceProperties(formConfig: FormConfig): string[] {
  let results: string[] = [];
  for (const key of Object.keys(formConfig.properties)) {
    const property = formConfig.properties[key];
    if (property.widget === 'shopify') {
      results.push(key);
    }

    if (property.properties) {
      results = results.concat(getServiceProperties(property));
    }
  }
  return results;
}

export function removeResolverProperties(
  input: Record<string, unknown>,
  resolverProperties: string[]
): Record<string, unknown> {
  for (const key of Object.keys(input)) {
    const prop = input[key];
    if (resolverProperties.includes(key)) {
      delete input[key];
    } else if (isArray(prop)) {
      input[key] = prop.map((arrayItem) => {
        return typeof arrayItem === 'string' ? arrayItem : removeResolverProperties(arrayItem, resolverProperties);
      });
    } else if (isRecord(prop) && typeof prop !== 'string') {
      input[key] = removeResolverProperties(prop, resolverProperties);
    }
  }
  return input;
}

export function getSaveSaga<Action extends PayloadAction<string, SavePayload>>({
  createCallback,
  updateCallback
}: SaveSagaParams<Action>) {
  return function* (action: Action): SagaIterator {
    let { isNew, structure, input, source, ...meta } = action.payload;

    if (isNew) {
      assert(source, 'source is required when creating new content');
      const fulfilledPayload = yield call(
        createContentSaga,
        createContent.requested({ input, structure: [] }, { ...meta, source })
      );

      if (createCallback && fulfilledPayload) {
        yield call(createCallback, { fulfilledPayload, action });
      }
    } else {
      const contentState: ContentState = yield meta.isSingleton
        ? select(getSingletonContentState, {
            shapeId: meta.shapeId,
            contentId: meta.shapeId,
            locale: meta.locale
          })
        : select(getContentState, {
            shapeId: meta.shapeId,
            contentId: meta.contentId,
            locale: meta.locale
          });

      input._version = contentState.data?._version;

      const projectSchema: ProjectSchemaJSON = yield select(getSchema);
      const shape = getShapeById(projectSchema, meta.shapeId);

      // Pass along service ID since this is used to differentiate creates vs updates of service data
      if (shape && contentState.data) {
        for (const serviceInfo of getServiceObjectFields(projectSchema, shape, 'shopify')) {
          const generator = serviceInfo.generators[SERVICE_OBJECT_PATTERN_NAME];

          const { idFieldName } = generator;
          const idPath = [...serviceInfo.path, idFieldName];
          const existingId = get(contentState.data, idPath);
          const inputId = get(input, idPath);
          input = produce(input, (draft) => {
            if (existingId !== undefined && inputId === undefined) {
              set(draft, idPath, existingId);
            }
          });
        }
      }

      assert(meta.contentId, 'contentId is required for content update');
      const fulfilledPayload = yield call(
        updateContentSaga,
        updateContent.requested({ input, structure }, { ...meta, contentId: meta.contentId })
      );
      if (updateCallback && fulfilledPayload) {
        yield call(updateCallback, { fulfilledPayload, action });
      }
    }
  };
}

export function* getShapePath(shapeId: string): SagaIterator {
  const projectSchema: ProjectSchemaJSON = yield select(getSchema);
  const branchPath: string = yield select(getCurrentBranchPath);
  return `${branchPath}/data/${getShapeById(projectSchema, shapeId)?.name}`;
}

export function* saveCallback({ action }: SaveCallbackParams<SaveAction>): SagaIterator {
  const { shapeId } = action.payload;
  yield call(waitForIndexing, shapeId);
  const shapePath = yield call(getShapePath, shapeId);
  yield put(push(shapePath));
}

export function* saveAndContinueCallback({
  fulfilledPayload,
  action
}: SaveCallbackParams<SaveAndContinueAction>): SagaIterator {
  const { shapeId } = action.payload;
  const { contentId } = fulfilledPayload;
  const contentTypePath = yield call(getShapePath, shapeId);
  yield put(push(`${contentTypePath}/${contentId}`));
}

export function* saveAndDuplicateCallback({
  fulfilledPayload,
  action
}: SaveCallbackParams<SaveAndDuplicateAction>): SagaIterator {
  const { structure, input, shapeId, locale } = action.payload;
  const { contentId } = fulfilledPayload;
  const { contentId: newId, response } = yield call(
    duplicateContentSaga,
    duplicateContent.requested({ structure, input }, { contentId, shapeId, locale })
  );
  const values = response[shapeId][newId];
  const projectSchema: ProjectSchemaJSON = yield select(getSchema);
  const shape = getShapeById(projectSchema, shapeId);
  if (!shape) {
    throw new Error(`Missing shape ${shapeId}`);
  }

  const shapePath = yield call(getShapePath, shapeId);
  yield put(push(`${shapePath}/${newId}`));

  const duplicateContentValues = omitBy(values, (_, key) => key.startsWith('_'));
  const defaultContent = yield select(getDefaultContent, { shapeId });
  const duplicatedContent = { ...defaultContent, ...duplicateContentValues };

  const inputSchema = yield select(getDataEditorSchema, {
    shapeName: shape.name
  });
  yield put(
    registerForm(FORM_NAME, inputSchema, duplicatedContent, {
      initialDirty: Object.keys(duplicateContentValues)
    })
  );
}

export function* saveAndAddAnotherCreateCallback({
  action
}: SaveCallbackParams<SaveAndAddAnotherAction>): SagaIterator {
  const { shapeId } = action.payload;
  const projectSchema: ProjectSchemaJSON = yield select(getSchema);
  const shape = getShapeById(projectSchema, shapeId);
  if (!shape) {
    throw new Error(`Missing shape ${shapeId}`);
  }

  const defaultContent = yield select(getDefaultContent, { shapeId });
  const inputSchema = yield select(getDataEditorSchema, {
    shapeName: shape.name
  });
  yield put(registerForm(FORM_NAME, inputSchema, defaultContent));
}

export function* saveAndAddAnotherUpdateCallback({
  action
}: SaveCallbackParams<SaveAndAddAnotherAction>): SagaIterator {
  const { shapeId } = action.payload;
  const shapePath = yield call(getShapePath, shapeId);
  yield put(push(`${shapePath}/create`));
}

function* watchSubmitFormFailure(action: SubmitFormFailureAction): SagaIterator {
  const { formName } = action.meta;
  if (formName === FORM_NAME) {
    yield put(showMessage('error', "Couldn't save entry: one or more invalid fields.", 5000));
  }
}

const saveSaga = getSaveSaga({
  createCallback: saveCallback,
  updateCallback: saveCallback
});
const saveAndContinueSaga = getSaveSaga({
  createCallback: saveAndContinueCallback
});

const saveAndAddAnotherSaga = getSaveSaga({
  createCallback: saveAndAddAnotherCreateCallback,
  updateCallback: saveAndAddAnotherUpdateCallback
});

const saveAndDuplicateSaga = getSaveSaga({
  createCallback: saveAndDuplicateCallback,
  updateCallback: saveAndDuplicateCallback
});

export function* contentUpdatedSaga({ payload }: ContentUpdatedAction): SagaIterator {
  const { contentId, contentTypeId: shapeId, contentVersion } = payload;
  const projectSchema: ProjectSchemaJSON = yield select(getSchema);
  const shape = getShapeById(projectSchema, shapeId);
  if (shape) {
    const isSingleton = Boolean(shape.model && shape.model.type === 'single');
    yield put(invalidateContent({ shapeId, isSingleton, contentId, contentVersion }));
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeEvery(getContent.requested, getContentSaga),
    takeEvery(createContent.requested, createContentSaga),
    takeEvery(updateContent.requested, updateContentSaga),
    takeEvery(deleteContent.requested, deleteContentSaga),
    takeEvery(duplicateContent.requested, duplicateContentSaga),
    takeLatest(SUBMIT_FORM_FAILURE, watchSubmitFormFailure),
    takeLatest(save, saveSaga),
    takeLatest(saveAndContinue, saveAndContinueSaga),
    takeLatest(saveAndAddAnother, saveAndAddAnotherSaga),
    takeLatest(saveAndDuplicate, saveAndDuplicateSaga),
    takeLatest(contentUpdated, contentUpdatedSaga)
  ]);
}
